import React, { Suspense, lazy } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { Toaster } from 'sonner';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { ThemeProvider } from './contexts/ThemeContext';
import { HelpProvider } from './contexts/HelpContext';
import { MobileChromeProvider } from './contexts/MobileChromeContext';
import ProtectedRoute from './components/ProtectedRoute';
import Layout from './components/Layout';
import ScrollToTop from './components/ScrollToTop';
import ErrorBoundary from './components/ErrorBoundary';

// Route-level code splitting. Each page ships in its own chunk so the
// login/dashboard path doesn't pay for screens the user hasn't opened yet.
const Login = lazy(() => import('./pages/Login'));
const SetPassword = lazy(() => import('./pages/SetPassword'));
const Dashboard = lazy(() => import('./pages/Dashboard'));
const QRScanner = lazy(() => import('./pages/QRScanner'));
const BarrelDetail = lazy(() => import('./pages/BarrelDetail'));
const TastingLog = lazy(() => import('./pages/TastingLog'));
const BarrelAnalytics = lazy(() => import('./pages/BarrelAnalytics'));
const CreateBarrel = lazy(() => import('./pages/CreateBarrel'));
const BarrelCatalog = lazy(() => import('./pages/BarrelCatalog'));
const Stats = lazy(() => import('./pages/Stats'));
const UserProfile = lazy(() => import('./pages/UserProfile'));
const AdminDashboard = lazy(() => import('./pages/AdminDashboard'));
const LiquidDetail = lazy(() => import('./pages/LiquidDetail'));
const Blends = lazy(() => import('./pages/Blends'));
const CreateVatBlend = lazy(() => import('./pages/CreateVatBlend'));
const Projects = lazy(() => import('./pages/Projects'));
const ProjectCreate = lazy(() => import('./pages/Projects/ProjectCreate'));
const ProjectEdit = lazy(() => import('./pages/Projects/ProjectEdit'));
const ProjectDetail = lazy(() => import('./pages/Projects/ProjectDetail'));
const Templates = lazy(() => import('./pages/Templates'));

const PageFallback = () => (
  <div className="min-h-screen bg-white dark:bg-dark-900 flex items-center justify-center">
    <div className="text-center">
      <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-amber-600 mx-auto mb-3"></div>
      <p className="text-gray-600 dark:text-gray-400 text-sm">Loading…</p>
    </div>
  </div>
);

function AppContent() {
  const { loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen bg-white dark:bg-dark-900 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-amber-600 mx-auto mb-4"></div>
          <p className="text-gray-600 dark:text-gray-400">Loading...</p>
        </div>
      </div>
    );
  }

  return (
    <Router>
      <ScrollToTop />
      <ErrorBoundary>
        <Suspense fallback={<PageFallback />}>
          <Routes>
            <Route path="/login" element={<Login />} />
            <Route path="/set-password" element={<SetPassword />} />
            {/* `/reset-password` is an alias kept in case a stale email
                contains the older link; both flows funnel into the same
                page since they share the same Firebase oobCode. */}
            <Route path="/reset-password" element={<SetPassword />} />
            <Route path="/" element={
              <ProtectedRoute>
                <Layout />
              </ProtectedRoute>
            }>
              <Route index element={<Dashboard />} />
              <Route path="scan" element={<QRScanner />} />
              <Route path="create" element={<CreateBarrel />} />
              <Route path="catalog" element={<BarrelCatalog />} />
              <Route path="stats" element={<Stats />} />
              <Route path="barrel/:barrelId" element={<BarrelDetail />} />
              <Route path="barrel/:barrelId/tasting" element={<TastingLog />} />
              <Route path="barrel/:barrelId/analytics" element={<BarrelAnalytics />} />
              <Route path="liquid/:liquidId" element={<LiquidDetail />} />
              <Route path="liquid/:liquidId/tasting" element={<TastingLog />} />
              <Route path="blends" element={<Blends />} />
              <Route path="blends/new-vat" element={<CreateVatBlend />} />
              <Route path="projects" element={<Projects />} />
              <Route path="projects/new" element={<ProjectCreate />} />
              <Route path="projects/:projectId" element={<ProjectDetail />} />
              <Route path="projects/:projectId/edit" element={<ProjectEdit />} />
              <Route path="templates" element={<Templates />} />
              <Route path="profile" element={<UserProfile />} />
              <Route path="admin" element={<AdminDashboard />} />
              {/* Retired/unknown paths (e.g. old /kentucky bookmarks)
                  land on the Dashboard instead of a blank screen. */}
              <Route path="*" element={<Navigate to="/" replace />} />
            </Route>
          </Routes>
        </Suspense>
      </ErrorBoundary>
    </Router>
  );
}

function App() {
  return (
    <ThemeProvider>
      <AuthProvider>
        <HelpProvider>
          <MobileChromeProvider>
            <AppContent />
            {/* Toaster lives here (outside Router but inside ThemeProvider)
             *  so toasts persist across navigation and auto-pick up the
             *  app's dark-mode class via theme="system". Bottom-right
             *  matches where the Sentry "Report a problem" pill sits;
             *  sonner stacks above on z-index by default. */}
            <Toaster
              theme="system"
              position="bottom-right"
              richColors
              closeButton
            />
          </MobileChromeProvider>
        </HelpProvider>
      </AuthProvider>
    </ThemeProvider>
  );
}

export default App;